import React, { useContext } from 'react';
import { ScrollView, View } from 'react-native';
import {
  Button, DataTable, IconButton, List, Text,
} from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';
import { useMutation } from '@apollo/client';
import { Popover } from 'native-base';
import { useToast } from 'react-native-toast-notifications';
import { ROLE_NAMES, SCREEN_NAMES } from '../../../utils/constant';
import { DeleteUserMutation, UPDATE_USER_ROLE } from '../../../gql/user/mutation';
import { AuthContext } from '../../../context/AuthContext';
import { ModalContext } from '../../../context/ModalContext';
import ConfirmationModal from '../../../modals/Confirmation/ConfirmationModal';
import LoadingIndicator from '../../../components/commons/LoadingIndicator';
import UserAvatarBadge from '../../../components/commons/UserAvatarBadge';
import useDesktopLayout from '../../../hooks/useDesktopLayout';

function CoachesTab({
  users, searchQuery, loading, refetch,
}) {
  const navigation = useNavigation();
  const toast = useToast();
  const { authState } = useContext(AuthContext);
  const { displayConfirmationModal, toggleConfirmationModal } = useContext(ModalContext);
  const isSuperAdmin = authState.role === 'SUPERADMIN';
  const isDesktop = useDesktopLayout();

  const coaches = users
    .filter((u) => u.role === ROLE_NAMES.COACH)
    .filter((u) => u.firstName?.toLowerCase().includes(searchQuery)
      || u.lastName?.toLowerCase().includes(searchQuery));

  const [deleteOneUser] = useMutation(DeleteUserMutation, {
    onCompleted: (_data) => {
      toast.show(_data.deleteOneUser.message);
      refetch();
    },
  });

  const [updateUserRole] = useMutation(UPDATE_USER_ROLE, {
    onCompleted: (_data) => {
      toast.show(_data.updateUserRole.message);
      refetch();
    },
  });

  return (
    <>
      {displayConfirmationModal.display && <ConfirmationModal />}
      {loading && <LoadingIndicator text="Chargement..." />}
      <ScrollView style={{ flex: 1 }}>
        {isDesktop ? (
          <DataTable>
            <DataTable.Header>
              <DataTable.Title>{' '}</DataTable.Title>
              <DataTable.Title>Nom</DataTable.Title>
              <DataTable.Title>Email</DataTable.Title>
              <DataTable.Title>Licence</DataTable.Title>
              <DataTable.Title numeric>Actions</DataTable.Title>
            </DataTable.Header>
            {coaches.map((d) => (
              <DataTable.Row key={d.id}>
                <DataTable.Cell>
                  {isSuperAdmin ? (
                    <Popover
                      trigger={(triggerProps) => (
                        <UserAvatarBadge
                          user={d}
                          icon="account-hard-hat"
                          triggerProps={triggerProps}
                        />
                      )}
                    >
                      <Popover.Content>
                        <Popover.Body>
                          <Button
                            contentStyle={{ justifyContent: 'flex-start' }}
                            onPress={() => updateUserRole({ variables: { id: d.id, role: ROLE_NAMES.USER } })}
                          >
                            Rétrograder en utilisateur
                          </Button>
                        </Popover.Body>
                      </Popover.Content>
                    </Popover>
                  ) : (
                    <UserAvatarBadge user={d} icon="account-hard-hat" />
                  )}
                </DataTable.Cell>
                <DataTable.Cell>{`${d.firstName} ${d.lastName}`}</DataTable.Cell>
                <DataTable.Cell>{d.email}</DataTable.Cell>
                <DataTable.Cell>{d.profile?.license ?? 'Aucune licence'}</DataTable.Cell>
                <DataTable.Cell numeric>
                  <View style={{ flexDirection: 'row' }}>
                    <IconButton
                      icon="eye"
                      onPress={() => navigation.navigate({ name: SCREEN_NAMES.PROFILE, params: { email: d.email } })}
                    />
                    {isSuperAdmin && (
                      <IconButton
                        icon="delete"
                        onPress={() => toggleConfirmationModal({
                          title: 'Confirmation',
                          message: "Voulez-vous vraiment supprimer définitivement l'utilisateur ? Cette action est irréversible.",
                          handleSubmit: async () => { await deleteOneUser({ variables: { email: d.email } }); },
                        })}
                      />
                    )}
                  </View>
                </DataTable.Cell>
              </DataTable.Row>
            ))}
          </DataTable>
        ) : coaches.map((d) => (
          <List.Item
            key={d.id}
            title={`${d.firstName} ${d.lastName}`}
            description={(props) => (
              <View>
                <Text {...props}>{d.email}</Text>
                <Text {...props} style={{ fontWeight: 'bold' }}>{d.profile?.license ?? 'Aucune licence'}</Text>
              </View>
            )}
            left={(props) => {
              if (isSuperAdmin) {
                return (
                  <Popover
                    trigger={(triggerProps) => (
                      <UserAvatarBadge
                        user={d}
                        icon="account-hard-hat"
                        style={props.style}
                        triggerProps={triggerProps}
                      />
                    )}
                  >
                    <Popover.Content>
                      <Popover.Body>
                        <Button
                          contentStyle={{ justifyContent: 'flex-start' }}
                          onPress={() => updateUserRole({ variables: { id: d.id, role: ROLE_NAMES.USER } })}
                        >
                          Rétrograder en utilisateur
                        </Button>
                      </Popover.Body>
                    </Popover.Content>
                  </Popover>
                );
              }
              return <UserAvatarBadge user={d} icon="account-hard-hat" style={props.style} />;
            }}
            right={(props) => (
              <>
                <IconButton
                  {...props}
                  icon="eye"
                  onPress={() => navigation.navigate({ name: SCREEN_NAMES.PROFILE, params: { email: d.email } })}
                />
                {isSuperAdmin && (
                  <IconButton
                    {...props}
                    icon="delete"
                    onPress={() => toggleConfirmationModal({
                      title: 'Confirmation',
                      message: "Voulez-vous vraiment supprimer définitivement l'utilisateur ? Cette action est irréversible.",
                      handleSubmit: async () => { await deleteOneUser({ variables: { email: d.email } }); },
                    })}
                  />
                )}
              </>
            )}
          />
        ))}
        {coaches.length === 0 && !loading && (
          <Text style={{ textAlign: 'center', marginTop: 32, opacity: 0.5 }}>
            Aucun coach trouvé
          </Text>
        )}
      </ScrollView>
    </>
  );
}

export default CoachesTab;
